/**
 * Hyphenating a marker name that is wider than the label column.
 *
 * `.ref-range-category-wrap` asks the browser for this with `hyphens: auto`,
 * and on screen the browser obliges. THE PDF IS NOT LAID OUT BY THE BROWSER:
 * html2canvas rasterizes it, and html2canvas implements UAX-14 line breaking
 * and the `word-break` / `overflow-wrap` properties only — the CSS `hyphens`
 * property is not among the styles it reads. So "Polymorphonuclear Cells"
 * hyphenated on screen and broke bare in the export, which is the same gap that
 * made `word-break: break-word` useless here.
 *
 * The answer is the one this report uses everywhere else: work it out in JS and
 * put a REAL hyphen in the text, which every renderer draws because it is just a
 * character. A break is allowed after a hyphen under UAX-14 (class HY), so the
 * browser and html2canvas both break in the same place.
 *
 * TYPESETTING, NEVER DATA. The hyphen is inserted at render time and belongs to
 * the printed line. The marker's name is stored, shown in My Markers, carried
 * into the comparison table and written to every export without it.
 */

/**
 * Character widths at the label's 12px bold, MEASURED in Chrome across the
 * alphabet rather than averaged: 'm' is 10.67px and 'l' is 3.33, so one average
 * either breaks short words that fit or overruns the ones it was written for.
 *
 * TWELVE, not the fourteen `.ref-range-category` asks for. This panel's table
 * carries Bootstrap's `.table` class, and `.table td` in index.css sets
 * `font-size: 12px` — a class plus an element, which out-specifies the bare
 * `.ref-range-category`. Measured on the rendered cell: the label column is
 * 110px with no padding of its own and its text is 12px bold.
 *
 * Widths taken at 14px are about a sixth too wide, and a sixth too wide is a
 * name that FITS being told it does not. "Staphylococcus" is 92px in a 110px
 * column and printed as "Staphyloco-ccus", with the dash mid-line where the
 * text never breaks at all.
 *
 * The body's font is `sans-serif` (index.css), so these are Arial/Helvetica
 * bold advances. A client machine resolving a different face shifts them a
 * little, which is what the safety margin below absorbs.
 */
const CHAR_WIDTH: { [char: string]: number } = {
  a: 6.67, b: 7.33, c: 6.67, d: 7.33, e: 6.67, f: 4, g: 7.33, h: 7.33,
  i: 3.33, j: 3.33, k: 6.67, l: 3.33, m: 10.67, n: 7.33, o: 7.33, p: 7.33,
  q: 7.33, r: 4.67, s: 6.67, t: 4, u: 7.33, v: 6.67, w: 9.33, x: 6.67,
  y: 6.67, z: 6,
  A: 8.67, B: 8.67, C: 8.67, D: 8.67, E: 8, F: 7.33, G: 9.33, H: 8.67,
  I: 3.33, J: 6.67, K: 8.67, L: 7.33, M: 10, N: 8.67, O: 9.33, P: 8,
  Q: 9.33, R: 8.67, S: 8, T: 7.33, U: 8.67, V: 8, W: 11.33, X: 8,
  Y: 8, Z: 7.33,
  0: 6.67, 1: 6.67, 2: 6.67, 3: 6.67, 4: 6.67,
  5: 6.67, 6: 6.67, 7: 6.67, 8: 6.67, 9: 6.67,
  ' ': 3.33, '-': 4, '.': 3.33, ',': 3.33, ':': 4, '/': 3.33,
  '(': 4, ')': 4, '%': 10.67,
};
/** Anything unmeasured is charged as a wide lowercase letter. */
const DEFAULT_CHAR_WIDTH = 7.33;

/** The fixed width of the label column — see .ref-range-category-wrap. */
export const LABEL_COLUMN_PX = 110;

/**
 * The widest the label column goes in a panel of nothing but text results.
 *
 * 110px is a width the bar graphs set: the names are capped there so a long one
 * cannot widen the column and push the charts past the panel's right edge. A
 * section with no charts in it has nothing to protect and names half again as
 * long as any chart label — "Stool Gram-Negative Coccobacilli" is 193px, and at
 * 110 it printed on three lines with 150px of white space past the result it
 * was naming.
 *
 * 200 is where the names stop rather than a share of the panel: it clears the
 * longest of them with the safety margin on, and every pixel the column takes
 * is one the two value columns give up.
 */
export const TEXT_LABEL_COLUMN_MAX_PX = 200;

/**
 * Held back from the column so a word predicted to fit really does. The cell's
 * own padding is zeroed (`.labsmartz-chart-table td`, index.css) and measures
 * 0, so this margin is entirely for a client machine that resolves a different
 * face for `sans-serif` than the one these widths were measured in.
 */
export const LABEL_SAFETY_PX = 6;

export function labelTextWidth(text: string): number {
  let width = 0;
  for (const char of text) {
    width += CHAR_WIDTH[char] ?? DEFAULT_CHAR_WIDTH;
  }
  return width;
}

/**
 * How wide the label column should be for a given set of text-result names.
 *
 * Measured from the names themselves, so a panel of short ones keeps the 110px
 * the charts use and does not open a gap down the left of the card to hold
 * width nothing in it needs. One number for the whole panel — the column is a
 * COLUMN, and a per-row width is not one.
 */
export function textLabelColumnWidth(labels: string[]): number {
  const widest = labels.reduce(
    (max, label) => Math.max(max, labelTextWidth(label || '')),
    0,
  );
  return Math.min(
    TEXT_LABEL_COLUMN_MAX_PX,
    Math.max(LABEL_COLUMN_PX, Math.ceil(widest) + LABEL_SAFETY_PX),
  );
}

/**
 * Where to break a word too wide for the column.
 *
 * The longest leading piece that fits WITH its hyphen, pulled back to end on a
 * vowel where one is near. A width test alone cuts "Polymorphonucl-", which is
 * where the line runs out rather than where the word comes apart; the pull-back
 * gives "Polymorphonu-", a syllable a reader recognizes.
 *
 * Returns 0 when the word should be left alone.
 */
function breakPoint(word: string, budget: number): number {
  const hyphen = CHAR_WIDTH['-'];
  let widest = 0;
  let width = 0;
  for (let i = 0; i < word.length - 1; i++) {
    width += CHAR_WIDTH[word[i]] ?? DEFAULT_CHAR_WIDTH;
    if (width + hyphen > budget) {
      break;
    }
    widest = i + 1;
  }
  // Two letters either side at minimum, or the break reads as a typo.
  if (widest < 2 || word.length - widest < 2) {
    return 0;
  }
  const VOWELS = 'aeiouyAEIOUY';
  for (let i = widest; i >= Math.max(2, widest - 4); i--) {
    if (VOWELS.indexOf(word[i - 1]) >= 0 && word.length - i >= 2) {
      return i;
    }
  }
  return widest;
}

/**
 * The label as it should PRINT: any word too wide for the column broken with a
 * hyphen, everything else untouched.
 *
 * Words are hyphenated one piece at a time, so a name long enough to need two
 * breaks gets two.
 */
export function hyphenateLabel(
  label: string,
  columnPx: number = LABEL_COLUMN_PX,
): string {
  const budget = columnPx - LABEL_SAFETY_PX;
  if (!label || budget <= 0) {
    return label;
  }
  return label
    .split(' ')
    .map(word => {
      // A word with a hyphen in it already breaks where the author put it.
      if (word.indexOf('-') >= 0 || labelTextWidth(word) <= budget) {
        return word;
      }
      let rest = word;
      const pieces: string[] = [];
      // Bounded by the string: every pass either breaks the word or stops.
      while (labelTextWidth(rest) > budget) {
        const at = breakPoint(rest, budget);
        if (!at) {
          break;
        }
        pieces.push(`${rest.slice(0, at)}-`);
        rest = rest.slice(at);
      }
      pieces.push(rest);
      return pieces.join('');
    })
    .join(' ');
}
